import { MediaPickerButton } from '@/features/MediaLibrary/MediaPickerButton';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider } from 'react-hook-form';
import { toast } from 'sonner';
import type { VoxServiceCardResponse, VoxServiceProductResponse } from '@/core/generated/api';
import { ApiCardType } from '@/core/generated/api';
import { voxServiceService } from '@/entities/VoxService/api/voxServiceService';
import { VOX_SERVICE_CARD_TYPES } from '@/entities/VoxService/config/vox-service-form.config';
import { voxServiceToRequest, voxServiceToValues } from '@/entities/VoxService/mappers';
import { getAvailableVoxServiceProducts } from '@/entities/VoxService/model/availableProducts';
import { voxServiceSchema, type VoxServiceValues } from '@/entities/VoxService/model/schema';
import { useUnsavedForm } from '@/shared/hooks/useUnsavedForm';
import { confirmUnsavedChanges } from '@/shared/lib/confirmUnsavedChanges';
import { getErrorMessage } from '@/shared/lib/errors';
import { useCanAccessPermission } from '@/shared/model/adminAccess';
import { ActionButton } from '@/shared/ui/ActionButton/ActionButton';
import { Modal } from '@/shared/ui/Modal/Modal';
import { createRHFFields } from '@/shared/ui/RHFFields';
import { RequestError } from '@/shared/ui/RequestState';
import classes from './VoxServiceEditor.module.scss';

const { TextField, TextareaField, SelectField, NumberField, ToggleSwitchField } = createRHFFields<VoxServiceValues>('vox-service');

interface VoxServiceEditorProps {
    card?: VoxServiceCardResponse;
    cards: VoxServiceCardResponse[];
    products: VoxServiceProductResponse[];
    onSaved: () => void;
    onClose: () => void;
}

/** Редактирует настройки и показывает предпросмотр карточки услуги */
export const VoxServiceEditor = (
    { card, cards, products, onSaved, onClose }: VoxServiceEditorProps
) => {
    const form = useUnsavedForm<VoxServiceValues>({
        resolver: zodResolver(voxServiceSchema),
        defaultValues: voxServiceToValues(card),
    });

    const canSave = useCanAccessPermission(card
        ? 'admin.vox-services.edit'
        : 'admin.vox-services.create');

    const busy = form.formState.isSubmitting;
    const unchanged = Boolean(card) && !form.formState.isDirty;
    const values = form.watch();
    const options = getAvailableVoxServiceProducts(products, cards, card);

    /** Закрывает редактор с защитой несохранённых изменений */
    const close = (): void => {
        if (!busy) confirmUnsavedChanges(form.formState.isDirty, onClose);
    };

    /** Сохраняет настройки карточки */
    const submit = async (data: VoxServiceValues): Promise<void> => {
        if (!canSave || unchanged) return;

        if (cards.some(item => item.productId === data.productId && item.id !== card?.id)) {
            form.setError('productId', { message: 'Для этого продукта уже существует карточка' });

            return;
        }

        try {
            await voxServiceService.save(voxServiceToRequest(data, card?.version), card?.id);
            form.reset(data);
            toast.success('Карточка услуги сохранена');
            onSaved();
        } catch (error: unknown) {
            form.setError('root.server', { message: getErrorMessage(error, 'Не удалось сохранить карточку') });
        }
    };

    return <Modal
        visible
        title={card
            ? 'Изменить карточку услуги'
            : 'Новая карточка услуги'
        }
        size="lg"
        onHide={close}
        closable={!busy}
    >
        <FormProvider {...form}>
            <form
                className={classes.form}
                onSubmit={form.handleSubmit(submit)}
                noValidate
            >
                <div className={classes.layout}>
                    <div className={classes.fields}>
                        <SelectField
                            name="productId"
                            label="Базовый продукт"
                            options={options}
                            isRequired
                            disabled={busy}
                        />
                        <TextField
                            name="name"
                            label="Название карточки"
                            maxLength={100}
                            isRequired
                            disabled={busy}
                        />
                        <TextareaField
                            name="description"
                            label="Описание"
                            rows={3}
                            disabled={busy}
                        />
                        <div className={classes.pair}>
                            <SelectField
                                name="cardType"
                                label="Размер карточки"
                                options={VOX_SERVICE_CARD_TYPES}
                                isRequired
                                disabled={busy}
                            />
                            <NumberField
                                name="index"
                                label="Порядок"
                                min={-32768}
                                max={32767}
                                maxFractionDigits={0}
                                isRequired
                                disabled={busy}
                            />
                        </div>
                        <TextField
                            name="backgroundColor"
                            label="Цвет фона"
                            placeholder="#EEF5E9"
                            maxLength={7}
                            isRequired
                            disabled={busy}
                        />
                        <TextField
                            name="imageUrl"
                            label="Адрес изображения"
                            placeholder="https://…"
                            maxLength={2048}
                            disabled={busy}
                        />
                        <MediaPickerButton
                            disabled={busy}
                            onPick={url => form.setValue('imageUrl', url, {
                                shouldDirty: true,
                                shouldValidate: true
                            })}
                        />
                        <ToggleSwitchField
                            name="isShowServices"
                            label="Показывать в приложении"
                            disabled={busy}
                        />
                        <ToggleSwitchField
                            name="isUnderDevelopment"
                            label="В разработке"
                            disabled={busy}
                        />
                    </div>
                    <section
                        className={classes.preview}
                        aria-label="Предпросмотр карточки"
                    >
                        <h4>Предпросмотр</h4>
                        <div
                            className={classes.sample}
                            style={{
                                backgroundColor: /^#[0-9a-f]{6}$/i.test(values.backgroundColor)
                                    ? values.backgroundColor
                                    : undefined
                            }}
                        >
                            {/^https?:\/\//i.test(values.imageUrl) &&
                                <img
                                    src={values.imageUrl}
                                    alt=""
                                />
                            }
                        </div>
                        <strong>
                            {values.name || 'Название услуги'}
                        </strong>
                        <p>
                            {values.description || 'Описание появится здесь'}
                        </p>
                        <small>
                            {values.cardType === ApiCardType.Big
                                ? 'Большая карточка'
                                : 'Маленькая карточка'}
                        </small>
                        <small>
                            {values.isShowServices
                                ? 'Видна в приложении'
                                : 'Скрыта в приложении'}
                            {values.isUnderDevelopment
                                ? ' · В разработке'
                                : ''}
                        </small>
                        <p className={classes.hint}>На один базовый продукт — одна карточка. Чем меньше порядок, тем выше карточка в своей группе.</p>
                    </section>
                </div>
                {form.formState.errors.root?.server?.message &&
                    <RequestError
                        compact
                        message={form.formState.errors.root.server.message}
                    />
                }
                <div className={classes.actions}>
                    <ActionButton
                        type="button"
                        label="Отмена"
                        color="secondary"
                        onClick={close}
                        disabled={busy}
                    />
                    <ActionButton
                        type="submit"
                        label="Сохранить"
                        loading={busy}
                        disabled={!canSave || unchanged || busy}
                    />
                </div>
            </form>
        </FormProvider>
    </Modal>;
};
